import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ScrollText, RefreshCw, Copy, Check, AlertTriangle, ShieldCheck } from 'lucide-react';
import clsx from 'clsx';
import { releaseApi, sprintApi } from '../services/api';
import { useProjectStore } from '../store/useProjectStore';
import type { Release, Sprint } from '../types';

type Audience = 'internal' | 'public';

export default function ReleaseNotesPage() {
  const { releaseId } = useParams<{ releaseId: string }>();
  const { projects } = useProjectStore();

  const [release, setRelease] = useState<Release | null>(null);
  const [sprint, setSprint] = useState<Sprint | null>(null);
  const [notes, setNotes] = useState('');
  const [audience, setAudience] = useState<Audience>('internal');
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!releaseId) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const rel = await releaseApi.get(releaseId);
        if (cancelled) return;
        setRelease(rel);
        setNotes(pickNotes(rel, audience));

        if (rel.sprintId) {
          try {
            const sp = await sprintApi.get(rel.sprintId);
            if (!cancelled) setSprint(sp);
          } catch {
            /* a missing sprint shouldn't blank the page */
          }
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not load this release.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // audience is applied separately so switching it doesn't refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseId]);

  // Notes are generated server-side per audience, so switching audience
  // re-fetches rather than picking a stored field — that keeps the public view
  // authoritative about disclosure filtering instead of trusting cached text.
  useEffect(() => {
    if (!releaseId || !release) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await releaseApi.getNotes(releaseId, audience);
        if (cancelled) return;
        setNotes(result.notes);
        setStale(result.stale);
      } catch {
        if (!cancelled) setNotes(pickNotes(release, audience));
      }
    })();
    return () => { cancelled = true; };
  }, [releaseId, release, audience]);

  const regenerate = async () => {
    if (!releaseId) return;
    setRegenerating(true);
    setError(null);
    try {
      const result = await releaseApi.regenerateNotes(releaseId);
      setNotes(audience === 'public' ? result.publicNotes : result.notes);
      setStale(false);
      setRelease((r) => (r ? {
        ...r, notes: result.notes, publicNotes: result.publicNotes,
      } : r));
    } catch (e: any) {
      setError(e?.message || 'Could not regenerate notes.');
    } finally {
      setRegenerating(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(notes);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard denied — nothing useful to say */
    }
  };

  if (!releaseId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500">
        <ScrollText size={40} className="mb-3" />
        <p className="text-lg">Select a release to read its notes</p>
        <p className="text-sm mt-1">
          Pick an organisation, then a project, then a release.
        </p>
      </div>
    );
  }

  if (loading && !release) {
    return (
      <div className="p-8 text-sm text-gray-500 dark:text-gray-400">
        Loading release…
      </div>
    );
  }

  if (error && !release) {
    return (
      <div className="p-8">
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (!release) return null;

  const project = projects.find((p) => p._id === release.projectId);
  const date = release.plannedDate || release.releaseDate;

  return (
    <div className="max-w-4xl mx-auto px-8 py-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-1">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ScrollText size={18} className="text-accent shrink-0" />
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 truncate">
              {release.version}
            </h1>
            <span
              className={clsx(
                'text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full',
                release.status === 'released'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                  : release.status === 'cancelled'
                  ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
              )}
            >
              {release.status}
            </span>
          </div>
          {release.name && (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              {release.name}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <div className="flex rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden">
            {(['internal', 'public'] as Audience[]).map((a) => (
              <button
                key={a}
                onClick={() => setAudience(a)}
                className={clsx(
                  'px-2.5 py-1 text-xs capitalize transition-colors',
                  audience === a
                    ? 'bg-accent text-white'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700',
                )}
              >
                {a}
              </button>
            ))}
          </div>
          <button
            onClick={copy}
            disabled={!notes}
            className="p-1.5 rounded border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
            title="Copy notes"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
          <button
            onClick={regenerate}
            disabled={regenerating}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-accent text-white text-xs font-medium hover:bg-accent/90 disabled:opacity-50"
          >
            <RefreshCw size={13} className={regenerating ? 'animate-spin' : ''} />
            Regenerate
          </button>
        </div>
      </div>

      {/* Meta line */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
        {project && <span>Project: {project.name}</span>}
        {sprint && <span>Sprint: {sprint.name}</span>}
        {date && <span>Date: {date}</span>}
        {release.releaseType === 'standalone' && (
          <span className="text-amber-600 dark:text-amber-400">
            Off-cycle release
          </span>
        )}
      </div>

      {audience === 'public' && (
        <div className="flex items-start gap-2 mb-4 px-3 py-2 rounded-md bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 text-xs">
          <ShieldCheck size={14} className="shrink-0 mt-0.5" />
          <span>
            Customer-facing view. Security items marked <strong>internal</strong> or{' '}
            <strong>embargoed</strong> are excluded, along with CVSS scores and
            remediation status. This is what an external reader sees.
          </span>
        </div>
      )}

      {stale && (
        <div className="flex items-start gap-2 mb-4 px-3 py-2 rounded-md bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-xs">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>
            The release scope changed since these notes were saved — regenerate
            to pick up the difference.
          </span>
        </div>
      )}

      {error && (
        <p className="mb-4 text-xs text-red-600 dark:text-red-400">{error}</p>
      )}

      {/* Notes */}
      {notes ? (
        <article className="prose prose-sm dark:prose-invert max-w-none">
          <ReactMarkdown>{notes}</ReactMarkdown>
        </article>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400 dark:text-gray-500">
          <ScrollText size={32} className="mb-2" />
          <p className="text-sm">No notes generated for this release yet.</p>
          <button
            onClick={regenerate}
            className="mt-3 text-xs text-accent hover:underline"
          >
            Generate now
          </button>
        </div>
      )}
    </div>
  );
}

function pickNotes(release: Release, audience: Audience): string {
  if (audience === 'public') return release.publicNotes || release.notes || '';
  return release.notes || '';
}
